import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, CalendarCog, Loader2, CheckCircle } from "lucide-react";
import { PetPolicyDialog } from "@/components/PetPolicyDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OverrideRow = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 text-sm cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid="booking-override-checkbox" />
    {label}
  </label>
);

export const AddBookingDialog = ({ getAuthHeaders, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    guest_name: "", guest_email: "", guest_phone: "",
    check_in: "", check_out: "", guest_count: "2",
    amount_paid: "0", notes: "", force: false,
    early_checkin: false, late_checkout: false,
  });

  const reset = () => setForm({
    guest_name: "", guest_email: "", guest_phone: "",
    check_in: "", check_out: "", guest_count: "2",
    amount_paid: "0", notes: "", force: false,
    early_checkin: false, late_checkout: false,
  });

  const submit = async () => {
    if (!form.guest_name || !form.guest_email || !form.check_in || !form.check_out) {
      toast.error("Guest name, email, check-in and check-out are required");
      return;
    }
    if (form.check_out <= form.check_in) {
      toast.error("Check-out must be after check-in");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/bookings/manual`, {
        guest_name: form.guest_name,
        guest_email: form.guest_email,
        guest_phone: form.guest_phone,
        check_in: form.check_in,
        check_out: form.check_out,
        guest_count: parseInt(form.guest_count) || 1,
        amount_paid: parseFloat(form.amount_paid) || 0,
        notes: form.notes || null,
        force: form.force,
        early_checkin: form.early_checkin,
        late_checkout: form.late_checkout,
      }, getAuthHeaders());
      toast.success(`Booking created — total $${res.data.total_price?.toLocaleString()}, balance $${res.data.balance_due?.toLocaleString()}`);
      reset();
      setOpen(false);
      onSaved?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409) {
        toast.error(`${detail} Tick "override" to proceed.`, { duration: 7000 });
      } else {
        toast.error(detail || "Failed to create booking");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <Button data-testid="add-booking-btn" onClick={() => setOpen(true)} className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white">
        <Plus className="w-4 h-4 mr-1" /> Add Booking
      </Button>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-booking-dialog">
        <DialogHeader>
          <DialogTitle>Add Manual Booking</DialogTitle>
          <DialogDescription>Create a booking directly — bypasses the online minimum-night rule.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Guest Name *</Label>
            <Input data-testid="mb-name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} placeholder="Full name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Email *</Label>
              <Input data-testid="mb-email" type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} placeholder="guest@example.com" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input data-testid="mb-phone" value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} placeholder="(555) 123-4567" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Check-in *</Label>
              <Input data-testid="mb-checkin" type="date" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Check-out *</Label>
              <Input data-testid="mb-checkout" type="date" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Guests</Label>
              <Input data-testid="mb-guests" type="number" min="1" value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Amount Already Paid</Label>
              <Input data-testid="mb-paid" type="number" min="0" step="0.01" value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea data-testid="mb-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional — e.g. booked via phone, special arrangement" />
          </div>
          <OverrideRow checked={form.force} onChange={(v) => setForm({ ...form, force: v })} label="Override minimum-night rule / date conflicts" />
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.early_checkin} onChange={(e) => setForm({ ...form, early_checkin: e.target.checked })} data-testid="mb-early-checkin" />
              Early check-in
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.late_checkout} onChange={(e) => setForm({ ...form, late_checkout: e.target.checked })} data-testid="mb-late-checkout" />
              Late checkout
            </label>
          </div>
          <p className="text-xs text-[#78716C]">Ticking early check-in / late checkout automatically blocks cleaning buffer days on the calendar.</p>
          <p className="text-xs text-[#78716C]">Total price is auto-calculated from your rates; you can adjust the balance later in Payments. Status is set to Confirmed.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="mb-submit" onClick={submit} disabled={saving} className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Create Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EditDatesDialog = ({ booking, getAuthHeaders, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addons, setAddons] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [showPetPolicy, setShowPetPolicy] = useState(false);
  const [pendingPetCount, setPendingPetCount] = useState(0);
  const [form, setForm] = useState({
    check_in: "", check_out: "", guest_count: "2",
    pet_count: 0, pet_policy_agreed: false,
    selected_addons: [], total_price: "", force: false,
  });

  const buildInitial = () => ({
    check_in: booking.check_in || "",
    check_out: booking.check_out || "",
    guest_count: String(booking.guest_count || 2),
    pet_count: booking.pet_count || 0,
    pet_policy_agreed: (booking.pet_count || 0) > 0 ? !!booking.pet_policy_agreed : false,
    selected_addons: (booking.selected_addons || []).filter(a => (a.id || a.type) !== "pet_fee"),
    total_price: "",
    force: false,
  });

  const openDialog = async () => {
    setForm(buildInitial());
    setPreview(null);
    setPreviewError("");
    setOpen(true);
    try {
      const res = await axios.get(`${API}/addons`);
      setAddons((res.data || []).filter(a => a.active !== false && a.id !== "pet_fee"));
    } catch { /* non-fatal */ }
  };

  const toggleAddon = (addon) => {
    setForm(prev => {
      const exists = prev.selected_addons.some(a => (a.id || a.type) === addon.id);
      const selected_addons = exists
        ? prev.selected_addons.filter(a => (a.id || a.type) !== addon.id)
        : [...prev.selected_addons, { id: addon.id, name: addon.name, price: addon.price, type: addon.type, quantity: 1, total: addon.price }];
      return { ...prev, selected_addons };
    });
  };

  const setPets = (n) => {
    if (n > 0 && !form.pet_policy_agreed) {
      setPendingPetCount(n);
      setShowPetPolicy(true);
      return;
    }
    setForm(prev => ({ ...prev, pet_count: n, pet_policy_agreed: n > 0 ? prev.pet_policy_agreed : false }));
  };

  const handlePetPolicyAccept = () => {
    setForm(prev => ({ ...prev, pet_count: pendingPetCount || 1, pet_policy_agreed: true }));
    setShowPetPolicy(false);
  };

  // Live Current -> New -> Difference preview
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/admin/bookings/${booking.id}/modify-preview`, {
          check_in: form.check_in,
          check_out: form.check_out,
          guest_count: parseInt(form.guest_count) || booking.guest_count,
          selected_addons: form.selected_addons,
          pet_count: form.pet_count,
          pet_policy_agreed: form.pet_count > 0 ? true : form.pet_policy_agreed,
        }, getAuthHeaders());
        setPreview(res.data);
        setPreviewError("");
      } catch (e) {
        setPreview(null);
        setPreviewError(e.response?.data?.detail || "");
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [open, form, booking.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (form.check_out <= form.check_in) {
      toast.error("Check-out must be after check-in");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/bookings/${booking.id}/modify`, {
        check_in: form.check_in,
        check_out: form.check_out,
        guest_count: parseInt(form.guest_count) || null,
        selected_addons: form.selected_addons,
        pet_count: form.pet_count,
        pet_policy_agreed: form.pet_count > 0 ? true : form.pet_policy_agreed,
        total_price: form.total_price !== "" ? parseFloat(form.total_price) : null,
        force: form.force,
      }, getAuthHeaders());
      const diff = res.data.difference || 0;
      const diffTxt = diff > 0 ? `+$${diff.toFixed(2)}` : diff < 0 ? `-$${Math.abs(diff).toFixed(2)}` : "no change";
      toast.success(`Booking updated — new total $${res.data.new_total?.toLocaleString()} (${diffTxt})`);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409) {
        toast.error(`${detail} Tick "override" to proceed.`, { duration: 7000 });
      } else {
        toast.error(detail || "Failed to update booking");
      }
    } finally {
      setSaving(false);
    }
  };

  const diff = preview?.difference ?? 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        data-testid={`edit-dates-${booking.id}`}
        variant="outline"
        size="sm"
        onClick={openDialog}
        className="text-xs whitespace-nowrap"
        title="Modify this reservation"
      >
        <CalendarCog className="w-3 h-3 mr-1" /> Modify Booking
      </Button>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-dates-dialog">
        <DialogHeader>
          <DialogTitle>Modify Booking — {booking.guest_name}</DialogTitle>
          <DialogDescription>Change dates, guests, pets, and add-ons — all in one place. The price difference updates live below.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Check-in</Label>
              <Input data-testid="ed-checkin" type="date" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Check-out</Label>
              <Input data-testid="ed-checkout" type="date" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Guests</Label>
            <Input data-testid="ed-guests" type="number" min="1" value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: e.target.value })} />
          </div>

          {/* Pets */}
          <div>
            <Label className="text-xs">Pets (max 2 — flat $150/stay)</Label>
            <div className="flex items-center gap-2 mt-1">
              {[0, 1, 2].map(n => (
                <Button key={n} type="button" size="sm"
                  variant={form.pet_count === n ? "default" : "outline"}
                  className={form.pet_count === n ? "bg-[#134E4A] hover:bg-[#0f3d3a] text-white" : ""}
                  onClick={() => setPets(n)}
                  data-testid={`ed-pets-${n}`}>
                  {n === 0 ? "No pets" : `${n} pet${n > 1 ? "s" : ""}`}
                </Button>
              ))}
            </div>
          </div>

          {/* Add-ons */}
          {addons.length > 0 && (
            <div>
              <Label className="text-xs">Add-ons</Label>
              <div className="space-y-2 mt-1">
                {addons.map(addon => {
                  const checked = form.selected_addons.some(a => (a.id || a.type) === addon.id);
                  return (
                    <label key={addon.id} className="flex items-center justify-between gap-3 p-2 border rounded cursor-pointer text-sm">
                      <span className="flex items-center gap-2">
                        <Checkbox checked={checked} onCheckedChange={() => toggleAddon(addon)} data-testid={`ed-addon-${addon.id}`} />
                        {addon.name}
                      </span>
                      <span className="text-[#134E4A] font-medium">${addon.price}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Live price panel */}
          <div className="bg-[#F0FDF9] border border-[#134E4A]/20 rounded p-3" data-testid="ed-price-panel">
            {previewError ? (
              <p className="text-sm text-red-600">{previewError}</p>
            ) : preview ? (
              <div className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-[#78716C]">Current total</span><span>${preview.current_total?.toLocaleString()}</span></div>
                <div className="flex justify-between font-medium"><span>New total</span><span data-testid="ed-new-total">${preview.new_total?.toLocaleString()}</span></div>
                <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                  <span>Difference</span>
                  <span data-testid="ed-diff" className={diff > 0 ? "text-amber-600" : diff < 0 ? "text-green-600" : ""}>
                    {diff > 0 ? "+" : ""}${diff.toFixed(2)}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#78716C]">Adjust any field to see the price difference.</p>
            )}
          </div>

          <div>
            <Label className="text-xs">Override total (optional — leave blank to auto-calculate)</Label>
            <Input data-testid="ed-total" type="number" min="0" step="0.01" placeholder="Auto" value={form.total_price} onChange={(e) => setForm({ ...form, total_price: e.target.value })} />
          </div>
          <OverrideRow checked={form.force} onChange={(v) => setForm({ ...form, force: v })} label="Override date conflicts" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="ed-submit" onClick={submit} disabled={saving} className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />} Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
      <PetPolicyDialog open={showPetPolicy} onOpenChange={setShowPetPolicy} onAccept={handlePetPolicyAccept} />
    </Dialog>
  );
};
